import { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import {
  useTheme,
  useMediaQuery,
  Box,
  Typography,
  Chip,
  Stack,
  Avatar,
  IconButton,
  Tooltip,
  TextField,
  MenuItem,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  alpha,
} from '@mui/material';
import {
  CheckCircle,
  Close,
  Cancel,
  HourglassEmpty,
  AddCircle,
  RemoveCircle,
  CalendarMonth,
} from '@mui/icons-material';
import api from 'state/axios';
import { buildPermissionMap, hasRight } from 'state/checkPermission';
import DeleteConfirm from 'components/DeleteConfirm';


/* ─── helpers ─── */
const AVATAR_COLORS = [
  '#0F6CBD', '#107C41', '#C43F1B', '#7B5EA7',
  '#CA5010', '#00897B', '#D13438', '#69797E',
];

const getAvatarColor = (str = '') => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = (str.charCodeAt(i) + ((hash << 5) - hash)) | 0;
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

const getInitials = (crewCode = '', crewName = '') => {
  if (crewCode) return crewCode;
  const parts = crewName.trim().split(/\s+/);
  return parts.length >= 2
    ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    : (crewName.slice(0, 2) || '??').toUpperCase();
};

const STATUS_CONFIG = {
  Approved: { color: 'success', icon: <CheckCircle fontSize="small" /> },
  Rejected: { color: 'error',   icon: <Cancel fontSize="small" /> },
  Pending:  { color: 'warning', icon: <HourglassEmpty fontSize="small" /> },
};


const RequestDetail = ({ open, onClose, request, onUpdateSuccess }) => {
  const user = useSelector((state) => state.user);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [saving, setSaving]           = useState(false);
  const [statusValue, setStatusValue] = useState('Pending');
  const [comments, setComments]       = useState([]);
  const [newComment, setNewComment]   = useState('');
  const [deleteOpen, setDeleteOpen]   = useState(false);

  const permissionMap = useMemo(() => buildPermissionMap(user?.permissions), [user?.permissions]);
  const canEdit = hasRight(permissionMap, 'CrewRequests', 'update', false);
  const canDelete  = hasRight(permissionMap, 'CrewRequests', 'delete', false);

  useEffect(() => {
    if (request) {
      setStatusValue(request.status?.status || 'Pending');
      setComments(
        Array.isArray(request.comments)
          ? request.comments.map((c) => ({ comment: c.comment || '' }))
          : []
      );
      setNewComment('');
    }
  }, [request]);

  if (!request) return null;

  const statusCfg  = STATUS_CONFIG[request.status?.status] ?? STATUS_CONFIG.Pending;
  const avatarBg   = getAvatarColor(request.crewCode || '');
  const abbr       = getInitials(request.crewCode || '', request.crewName || '');
  const handlerName = request.status?.handler
    ? `${request.status.handler.lastName?.toUpperCase() ?? ''} ${request.status.handler.firstName?.toUpperCase() ?? ''}`.trim()
    : null;

  const buildPayload = (overrideStatus) => ({
    status: { status: overrideStatus ?? statusValue },
    comments: [
      ...comments,
      ...(newComment.trim() ? [{ comment: newComment.trim() }] : []),
    ],
  });

  const handleSave = async (overrideStatus) => {
    try {
      setSaving(true);
      const url = `/crewrequests/process/${request._id}`;
      const payload = buildPayload(overrideStatus);
      await api.put(url, payload);
      toast.success('Request updated.');
      onUpdateSuccess();
      onClose();
    } catch (err) {
      toast.error(err?.message || 'Failed to update!');
    } finally {
      setSaving(false);
    }
  };

  const handleAddComment = async () => {
    const text = newComment.trim();
    if (!text) return;
    const updatedComments = [...comments, { comment: text }];
    setComments(updatedComments);
    setNewComment('');
    try {
      setSaving(true);
      await api.put(`/crewrequests/process/${request._id}`, {
        status: { status: statusValue },
        comments: updatedComments,
      });
      toast.success('Comment added.');
      onUpdateSuccess();
    } catch (err) {
      toast.error(err?.message || 'Failed to save comment!');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await api.patch(`/crewrequests/soft-delete/${request._id}`, {});
      toast.success('Request deleted.');
      onUpdateSuccess(true);
    } catch (err) {
      toast.error(err?.message || 'Failed to delete!');
    }
  };

  return (
    <>
      <Dialog
        fullWidth
        // maxWidth="md"
        // fullScreen={isMobile}
        open={open}
        onClose={onClose}
        sx={{ '& .MuiDialog-paper': { minWidth: isMobile ? '96%' : '55%' } }}
      >
        {/* ── Title ── */}
        <DialogTitle sx={{ position: 'relative' }}>
          <Typography variant="h6" fontWeight={600}>
            {request.title}
          </Typography>

          <Stack 
            direction="row" 
            spacing={1} 
            flexWrap="wrap" 
            sx={{ mt: 1 }}
          >            
            {request.requestType && (
              <Chip 
                size="small" 
                label={request.requestType} 
                variant="outlined" 
              />
            )}
            <Chip
              size="small"
              icon={<CalendarMonth sx={{ fontSize: 14 }} />}
              label={`${format(new Date(request.startDate), 'dd/MM/yyyy')} - ${format(new Date(request.endDate), 'dd/MM/yyyy')}`}
              variant="outlined"
            />
            <Chip
              size="small"
              icon={statusCfg.icon}
              label={request.status?.status ?? 'Pending'}
              color={statusCfg.color}
            />
          </Stack>
          
          <IconButton
            size="small"
            onClick={onClose}
            sx={{ 
              position: 'absolute', 
              top: '50%', 
              transform: 'translateY(-50%)', 
              right: 15 
            }}
          >
            <Close fontSize="small" />
          </IconButton>
        </DialogTitle>

        {/* ── Content ── */}
        <DialogContent 
          dividers 
          sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: 0, 
            p: 0, 
            overflow: 'hidden' 
          }}
        >

          {/* From/meta row */}
          <Box sx={{
            px: 3, py: 2,
            borderBottom: `1px solid ${theme.palette.divider}`,
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            flexShrink: 0,
            bgcolor: alpha(theme.palette.background.default, 0.5),
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Avatar 
                sx={{ 
                  width: 36, 
                  height: 36, 
                  bgcolor: avatarBg, 
                  fontSize: 13, 
                  fontWeight: 600 
                }}
              >
                {abbr}
              </Avatar>
              <Box>
                <Typography variant="body2" fontWeight={600}>
                  {request.crewName?.toUpperCase() || '-'}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  {format(new Date(request.createdAt), 'EEE, dd MMM yyyy HH:mm')}
                </Typography>
                {handlerName && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    Handled by <strong>{handlerName}</strong>
                    {request.status?.statusDate && (
                      <> at {format(new Date(request.status.statusDate), 'HH:mm dd/MM/yy')}</>
                    )}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>

          {/* Scrollable body */}
          <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 2.5 }}>

            {/* Email body */}
            <Box
              sx={{
                fontSize: '0.9rem', lineHeight: 1.75, color: 'text.primary',
                '& p': { my: 0.5 },
                '& ul, & ol': { pl: 2.5 },
                '& strong': { fontWeight: 600 },
              }}
              dangerouslySetInnerHTML={{ __html: request.content }}
            />

            {/* Comments */}
            <Divider sx={{ my: 2.5 }} />

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <Typography variant="subtitle2" fontWeight={600}>
                Comments
              </Typography>
              {comments.length > 0 && (
                <Box sx={{
                  bgcolor: 'action.selected', 
                  borderRadius: 10,
                  px: 0.8, 
                  fontSize: '0.7rem', 
                  fontWeight: 600,
                  color: 'text.secondary', 
                  lineHeight: 1.7,
                }}>
                  {comments.length}
                </Box>
              )}
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {comments.map((c, idx) => (
                <Box
                  key={idx}
                  sx={{
                    display: 'flex', 
                    alignItems: 'center',
                    gap: 1, 
                    bgcolor: theme.palette.action.hover,
                    borderRadius: 1, px: 1.5, py: 0.75,
                    border: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <TextField
                    size="small"
                    fullWidth
                    variant="standard"
                    placeholder={`Comment ${idx + 1}`}
                    value={c.comment}
                    slotProps={{ input: { disableUnderline: true } }}
                    onChange={(e) => {
                      const updated = [...comments];
                      updated[idx] = { ...c, comment: e.target.value };
                      setComments(updated);
                    }}
                    sx={{ '& .MuiInputBase-input': { fontSize: '0.875rem' } }}
                  />
                  <Tooltip title="Remove">
                    <IconButton
                      size="small"
                      disabled={saving}
                      onClick={async () => {
                        const updatedComments = comments.filter((_, i) => i !== idx);
                        setComments(updatedComments);
                        try {
                          setSaving(true);
                          await api.put(`/crewrequests/process/${request._id}`, {
                            status: { status: statusValue },
                            comments: updatedComments,
                          });
                          onUpdateSuccess();
                        } catch (err) {
                          toast.error(err?.message || 'Failed to remove comment!');
                        } finally {
                          setSaving(false);
                        }
                      }}
                      sx={{ color: 'text.disabled', '&:hover': { color: 'error.main' } }}
                    >
                      <RemoveCircle fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              ))}

              <Box sx={{
                display: 'flex', gap: 1, alignItems: 'center',
                border: `1px dashed ${theme.palette.divider}`,
                borderRadius: 1, px: 1.5, py: 0.75,
              }}>
                <TextField
                  size="small"
                  fullWidth
                  variant="standard"
                  placeholder="Add a comment… (Enter to add)"
                  value={newComment}
                  slotProps={{ input: { disableUnderline: true } }}
                  onChange={(e) => setNewComment(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey && newComment.trim()) {
                      e.preventDefault();
                      handleAddComment();
                    }
                  }}
                  sx={{ '& .MuiInputBase-input': { fontSize: '0.875rem' } }}
                />
                <Tooltip title="Add comment">
                  <span>
                    <IconButton
                      size="small"
                      color="primary"
                      disabled={!newComment.trim() || saving}
                      onClick={handleAddComment}
                    >
                      <AddCircle fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            </Box>
          </Box>
        </DialogContent>

        {/* ── Actions ── */}
        <DialogActions
          sx={{
            flexWrap: 'wrap',
            overflowX: 'auto',
          }}
        >
          {/* Process buttons - left side (hidden on xs) */}
          {canEdit && (
            <Box sx={{ display: { xs: 'none', sm: 'flex' }, gap: 1 }}>
              <Button
                size="small"
                variant={request.status?.status === 'Approved' ? 'contained' : 'outlined'}
                color="success"
                startIcon={<CheckCircle />}
                disabled={saving || request.status?.status === 'Approved'}
                onClick={() => handleSave('Approved')}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                Approve
              </Button>
              <Button
                size="small"
                variant={request.status?.status === 'Rejected' ? 'contained' : 'outlined'}
                color="error"
                startIcon={<Cancel />}
                disabled={saving || request.status?.status === 'Rejected'}
                onClick={() => handleSave('Rejected')}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                Reject
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<HourglassEmpty />}
                disabled={saving || request.status?.status === 'Pending'}
                onClick={() => handleSave('Pending')}
                sx={{ textTransform: 'none', color: 'text.secondary' }}
              >
                Reset
              </Button>
            </Box>
          )}

          {/* Spacer */}
          <Box sx={{ display: { xs: 'none', sm: 'block' }, flex: 1 }} />

          {/* Save section - right side */}
          {canEdit && (
            <TextField
              select
              size="small"
              label="Status"
              value={statusValue}
              onChange={(e) => setStatusValue(e.target.value)}
              sx={{
                display: { sm: 'none' },
                flex: 1,
              }}              
            >
              <MenuItem value="Pending">Pending</MenuItem>
              <MenuItem value="Approved">Approved</MenuItem>
              <MenuItem value="Rejected">Rejected</MenuItem>
            </TextField>
          )}
          
          {canDelete && (
            <Button
              variant="outlined"
              color="error"
              onClick={() => setDeleteOpen(true)}
            >
              Delete
            </Button>
          )}

          {canEdit && (
            <Button
              variant="outlined" 
              disabled={saving}
              onClick={() => handleSave()}
            >
              {saving ? <CircularProgress size={16} /> : 'Save'}
            </Button>
          )}

          <Button 
            variant="outlined" 
            color="secondary"
            onClick={onClose} 
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <DeleteConfirm
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={handleDelete}
        deleteType="single"
      />
    </>
  );
};

export default RequestDetail;
